/**
 * The sign-in method this browser last completed, shown as a quiet
 * "Último método usado" line on the sign-in card.
 *
 * Only the method name is kept — never an email, token or account hint — and
 * it is written only after a sign-in actually succeeded: Google once the
 * callback exchange completes, passkey once the ceremony is verified, email
 * once the password is accepted. A cancelled or rejected attempt never changes
 * it. Blocked storage (private mode, disabled site data) degrades to "no
 * preference" instead of throwing.
 */
export type SignInMethod = "google" | "passkey" | "email";

export const LAST_SIGN_IN_KEY = "tax.auth.lastMethod";

type MethodStorage = Pick<Storage, "getItem" | "setItem">;

function isSignInMethod(value: unknown): value is SignInMethod {
  return value === "google" || value === "passkey" || value === "email";
}

/** `window.localStorage` itself throws in some privacy modes; resolve it lazily. */
function defaultStorage(): MethodStorage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function readLastSignInMethod(
  storage: MethodStorage | null = defaultStorage(),
): SignInMethod | null {
  if (!storage) return null;
  try {
    const value = storage.getItem(LAST_SIGN_IN_KEY);
    return isSignInMethod(value) ? value : null;
  } catch {
    return null;
  }
}

export function recordLastSignInMethod(
  method: SignInMethod,
  storage: MethodStorage | null = defaultStorage(),
): void {
  if (!storage || !isSignInMethod(method)) return;
  try {
    storage.setItem(LAST_SIGN_IN_KEY, method);
  } catch {
    // Quota or privacy failure: the hint is only a convenience.
  }
}
